import Promise from 'bluebird';
import { useState, useMemo, useCallback, useEffect } from 'react';
import _ from 'underscore';

import {
  validateImageSizeAsync,
  validateImageWithDimension,
  getSmartCroppedImagesAsync,
  getImageValidationErrorMessage,
} from '@bingads-webui-campaign/image-experience-utils';
import { constants, newImageFromMTObject, addCropSettingsToCreativeImage } from '@bingads-webui-campaign-react/image-experience-model';
import { StockImageDataService } from '@bingads-webui-campaign/stock-image-data-service';
import { InvestImageDataService } from '@bingads-webui-campaign/invest-image-data-service';
import { VIEWS } from '@bingads-webui-campaign-react/add-media-panel-crud-ad';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';
import { notificationApi } from '@bingads-webui-react/notification-center';
import {
  SocialMediaRepository,
  SocialMediaDataSource,
  ImageRepository,
} from '@bingads-webui-campaign-react/image-experience';
import { MediaRepository } from '@bingads-webui-campaign/media-repository';
import { stringifySync, parseSync } from '@bingads-webui/safe-json';


import { assetLibraryLegacyI18nMap } from './legacy-i18n-map';

const imageOption = constants.defaultImageOption;

const removeNullUndefined = (obj) => {
  const newObj = {};
  _.keys(obj).forEach((key) => {
    const value = obj[key];
    if (_.isObject(obj[key])) {
      newObj[key] = removeNullUndefined(value);
    } else if (value !== null && value !== undefined) {
      newObj[key] = value;
    }
  });
  return newObj;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const serializeData = (data) => {
  const serialized = stringifySync(removeNullUndefined(data));
  return serialized.replace(/,/g, encodeURIComponent(','));
};

const processNewlyAddedImages = ({
  imgObjMT,
  imageSource,
  optionalCallback,
  isDisplayAds,
  isPortraitImage,
  isAudienceAdSmartCropEnabled,
  mediaRepository,
  isBrandKit = false,
}) => {
  const postValidationError = validateImageWithDimension(
    imgObjMT.Width,
    imgObjMT.Height,
    null,
    false,
    imageOption,
    false,
    false,
    isDisplayAds,
    isPortraitImage,
    false,
    false,
    false,
    false,
    false,
    false,
    isBrandKit
  );

  if (postValidationError) {
    postValidationError.isDueToResizing = true;

    throw postValidationError;
  }

  optionalCallback && optionalCallback(); // eslint-disable-line no-unused-expressions

  const imageObject = newImageFromMTObject(imgObjMT, imageSource);
  const croppedImagePlacementsPromise = getSmartCroppedImagesAsync({
    mediaRepository,
    originalImageToCrop: imageObject,
    isMultimediaAd: false,
    isMultimediaAdinMSANEnabled: false,
    imageOption,
    aspectRatioOption: constants.defaultAspectRatioOption,
    isDisplayAds,
    isPortraitImage,
    isAudienceAdSmartCropEnabled,
  });

  return croppedImagePlacementsPromise;
};

const genericErrorMsg = _TL_('We\'ve encountered an error and can\'t continue.');

export const useAssetLibraryImagePicker = ({
  cid,
  aid,
  oDataConfig,
  onNewImageSelected,
  i18n,
  permissions,
  isDisplayAds,
  isPortraitImage,
  serverContext,
  isAllUploadFlowImageUpscaleEnabled,
  isBrandKit = false,
}) => {
  const [imagePickerError, setImagePickerError] = useState('');
  const [isUploadImageUpscaleEnabled, setIsUploadImageUpscaleEnabled] = useState(false);

  useEffect(() => {
    if (isAllUploadFlowImageUpscaleEnabled) {
      isCopilotRegionRestricted({
        feature: 'AssetLibraryImagePicker',
        permissions,
        serverContext,
      })
        .then((isRestricted) => {
          if (!isRestricted) {
            setIsUploadImageUpscaleEnabled(isAllUploadFlowImageUpscaleEnabled);
          }
        });
    }
  }, [isAllUploadFlowImageUpscaleEnabled, permissions, serverContext]);

  const errorValidationI18n = useMemo(
    () => buildLegacyI18n(i18n, [assetLibraryLegacyI18nMap])
    , [i18n]
  );

  const minSize = useMemo(() => constants.getMinimumSizeForImage(null, false, imageOption, false, false, isDisplayAds, isPortraitImage), [isDisplayAds, isPortraitImage]);

  const minUploadSize = useMemo(() => {
    if (isUploadImageUpscaleEnabled && minSize.length > 1 && minSize[0] >= 352 && minSize[0] <= 703 && minSize[1] >= 184 && minSize[1] <= 368) {
      return [352, 184]; // It's a half of constants.DefaultImageMinimumSize 703x368
    }
    return [];
  }, [isUploadImageUpscaleEnabled, minSize]);

  const supportImageUpscale = useMemo(() => (isUploadImageUpscaleEnabled && !_.isEmpty(minUploadSize)), [isUploadImageUpscaleEnabled, minUploadSize]);

  const setImagePickerErrorWithErrorCodeError = useCallback((e) => {
    setImagePickerError(getImageValidationErrorMessage(e, errorValidationI18n) || i18n.getString(genericErrorMsg));
  }, [errorValidationI18n, i18n]);

  const clearImagePickerError = useCallback(() => {
    setImagePickerError('');
  }, []);

  const customMediaSourceConfig = useCallback(({ addMediaConfig }) => {
    let mediaSourceConfig = addMediaConfig;

    if (!permissions.IsSocialMediaImagesEnabled) {
      mediaSourceConfig = _.omit(mediaSourceConfig, VIEWS.SocialMedia);
    }

    return mediaSourceConfig;
  }, [
    permissions,
  ]);

  const imageRepository = useMemo(() => new ImageRepository(cid, aid, oDataConfig), [
    cid,
    aid,
    oDataConfig,
  ]);

  const stockImageDataService = useMemo(() => new StockImageDataService({
    cid,
    aid,
    oDataConfig,
    controller: 'StockImages',
  }), [
    cid,
    aid,
    oDataConfig,
  ]);

  const investImageDataService = useMemo(() => new InvestImageDataService({ cid, aid }), [cid, aid]);

  const socialMediaRepository = useMemo(() => new SocialMediaRepository({
    cid,
    aid,
    oDataConfig,
  }), [
    cid,
    aid,
    oDataConfig,
  ]);

  const socialMediaDataSource = useMemo(() => new SocialMediaDataSource({
    repository: socialMediaRepository,
  }), [
    socialMediaRepository,
  ]);

  const mediaRepository = useMemo(() => new MediaRepository({
    cid, aid, oDataConfig,
  }), [cid, aid, oDataConfig]);

  const isAudienceAdSmartCropEnabled = _.result(permissions.dynamic, 'AudienceAdSmartCrop', false);

  // don't have a great way to test uploading through webdriver right now
  // looking into it
  /* istanbul ignore next */
  const uploadImage = useCallback(({ imageData, updateCurrentProgress, name }) => { // eslint-disable-line arrow-body-style, max-len
    return validateImageSizeAsync(imageData, null)
      .then(() => imageRepository.uploadImageAsync(
        imageData,
        null,
        updateCurrentProgress,
        imageOption,
        name
      ))
      .then((response) => {
        if (!response || !response.imageId) {
          throw new Error(response && response[0] && response[0].Code);
        }
        return response;
      });
  }, [
    imageRepository,
  ]);

  // don't have a great way to test uploading through webdriver right now
  // looking into it
  /* istanbul ignore next */
  const processUploadedImage = useCallback(({ imageId, index, updateCurrentProgress }) => {
    const callback = () => updateCurrentProgress(index, 1);
    const imageSource = constants.imageSources.Upload;

    return imageRepository.getImageByIdAsync(imageId)
      .then(imgObjMT => processNewlyAddedImages({
        imgObjMT,
        imageSource,
        optionalCallback: callback,
        isDisplayAds,
        isPortraitImage,
        mediaRepository,
        isAudienceAdSmartCropEnabled,
        isBrandKit,
      }))
      .then((croppedImagePlacements) => {
        onNewImageSelected([croppedImagePlacements]);
        if (supportImageUpscale) {
          return _.first(croppedImagePlacements);
        }
        return null;
      });
  }, [imageRepository, isAudienceAdSmartCropEnabled, isDisplayAds, isPortraitImage, mediaRepository, onNewImageSelected, supportImageUpscale]);

  const onProcessUploadComplete = useCallback((args = []) => {
    const upscaledImages = _.filter(args, item => _.result(item, 'isImageUpscaled') === true);
    const count = upscaledImages.length;

    if (count === 1) {
      const imageName = _.get(upscaledImages, ['0', 'text']);
      if (imageName) {
        notificationApi.showInfo(i18n.getString(_TL_('We upscaled {{fileName}} to meet the minimum required file size. Please note that you can also upload a higher quality image if needed.'), { fileName: imageName }));
      }
    } else if (count > 1) {
      notificationApi.showInfo(i18n.getString(_TL_('We upscaled {{fileCount}} images to meet the minimum required file size. Please note that you can also upload higher quality images if needed.'), { fileCount: count }));
    }
  }, [i18n]);

  const addImageByUrl = useCallback((
    imageUrl,
    imageSource = constants.imageSources.WebsiteCarousel
    // eslint-disable-next-line arrow-body-style
  ) => {
    return validateImageSizeAsync(imageUrl, null, false, imageOption)
      .then(() => (
        imageRepository.bulkSaveImagesByUrl([{ url: imageUrl }])
          .then((ids) => {
            if (_.isObject(_.first(ids))) {
              throw new Error(ids[0].code);
            }

            return ids[0];
          })
      ))
      .then(id => imageRepository.getImageByIdAsync(id))
      .then(imgObjMT => processNewlyAddedImages({
        imgObjMT,
        imageSource,
        isDisplayAds,
        isPortraitImage,
        mediaRepository,
        isAudienceAdSmartCropEnabled,
        isBrandKit,
      }))
      .then((croppedImagePlacements) => {
        onNewImageSelected([croppedImagePlacements]);
        return {
          processedImage: croppedImagePlacements,
        };
      })
      .catch((e) => {
        setImagePickerErrorWithErrorCodeError(e);

        return {
          errorMessage: getImageValidationErrorMessage(e, errorValidationI18n) || i18n.getString(genericErrorMsg),
        };
      });
  }, [imageRepository, isDisplayAds, isPortraitImage, mediaRepository, isAudienceAdSmartCropEnabled, onNewImageSelected, setImagePickerErrorWithErrorCodeError, errorValidationI18n, i18n]);


  const addCreatives = useCallback(({ creativeData }) => {
    const getMTImages = async (ids) => {
      const images = await Promise.all(ids.map(id => imageRepository.getImageByIdAsync(id)));
      return images;
    };

    const getImages = async (imgObjsMT) => {
      const images = await Promise.all(imgObjsMT.map(imgObjMT => addCropSettingsToCreativeImage(imgObjMT)));
      return images;
    };

    // save original image first.
    const originalImageUrls = _.map(creativeData, (creative) => {
      const { template } = creative || {};

      const { image } = template || {};

      return { url: image.url };
    });


    return imageRepository.bulkSaveImagesByUrl(originalImageUrls)
      .then(ids => Promise.all(ids.map(id => imageRepository.getImageByIdAsync(id))))
      .then(images => creativeData.map((creative, index) => {
        const { template } = creative || {};

        // eslint-disable-next-line no-param-reassign
        creative.image = images[index].Url;
        template.image.url = images[index].Url;

        return {
          url: creative.url,
          metadata: serializeData(creative),
          baseId: images[index].Id,
          sourceType: 'AIGenerated',
        };
      }))
      .then(args => imageRepository.bulkSaveImagesByUrl(args))
      .then((ids) => {
        const error = _.find(ids, id => _.isObject(id));
        if (error) {
          throw new Error(error.code);
        }

        return ids;
      })
      .then(getMTImages)
      .then(getImages);
  }, [imageRepository]);

  const getStockImages = useCallback(args => stockImageDataService.getStockImages(args), [
    stockImageDataService,
  ]);

  const getInvestImages = useCallback(args => investImageDataService.getInvestAssets(args), [
    investImageDataService,
  ]);

  const onSelectStockImage = useCallback(({ selectedImageId }) => {
    const imageSource = constants.imageSources.Stock;

    return stockImageDataService.saveStockImage({
      stockImageId: selectedImageId,
      stockImageVendorId: 'ShutterStock',
    })
      .then(({ value }) => imageRepository.getImageByIdAsync(value))
      .then(imgObjMT => processNewlyAddedImages({
        imgObjMT,
        imageSource,
        isDisplayAds,
        isPortraitImage,
        mediaRepository,
        isAudienceAdSmartCropEnabled,
      }))
      .then((croppedImagePlacements) => {
        onNewImageSelected([croppedImagePlacements]);
      })
      .catch((e) => {
        setImagePickerErrorWithErrorCodeError(e);
      });
  }, [stockImageDataService, imageRepository, isDisplayAds, isPortraitImage, mediaRepository, isAudienceAdSmartCropEnabled, onNewImageSelected, setImagePickerErrorWithErrorCodeError]);

  const onImageSelected = useCallback((image, imageSource = constants.imageSources.Existing) => {
    const { id } = image || {};

    if (id) {
      return imageRepository.getImageByIdAsync(id)
        .then((imgObjMT) => {
          const { Metadata } = imgObjMT;

          let uriDecodedMetaData = Metadata;

          // while saving we only replace the ',' symbol with %2C. So we need to replace it back to ','
          if (Metadata) {
            uriDecodedMetaData = Metadata.replace(/%2C/g, ',');
          }

          const decodedMetadata = parseSync(uriDecodedMetaData);

          // if it is a display plus creative return the image
          if (decodedMetadata && decodedMetadata.template) {
            return {
              image: addCropSettingsToCreativeImage(imgObjMT),
              creativeData: {
                creativeMeta: decodedMetadata,
                creativeId: id,
              },
            };
          }

          return processNewlyAddedImages({
            imgObjMT,
            imageSource,
            isDisplayAds,
            isPortraitImage,
            mediaRepository,
            isAudienceAdSmartCropEnabled,
            isBrandKit,
          });
        })
        .then((croppedImagePlacements) => {
          onNewImageSelected([croppedImagePlacements]);
        })
        .catch((e) => {
          setImagePickerErrorWithErrorCodeError(e);
        });
    }

    return Promise.resolve();
  }, [imageRepository, isAudienceAdSmartCropEnabled, isDisplayAds, isPortraitImage, mediaRepository, onNewImageSelected, setImagePickerErrorWithErrorCodeError]);

  const onImagesSelected = useCallback((imageIds, imageSource = constants.imageSources.Existing) => {
    const addImagePromises = [];

    _.each(imageIds, (imageId) => {
      addImagePromises.push(imageRepository.getImageByIdAsync(imageId)
        .then(((imgObjMT) => {
          const { Metadata } = imgObjMT;

          let uriDecodedMetaData = Metadata;

          // while saving we only replace the ',' symbol with %2C. So we need to replace it back to ','
          if (Metadata) {
            uriDecodedMetaData = Metadata.replace(/%2C/g, ',');
          }

          const decodedMetadata = parseSync(uriDecodedMetaData);

          // if it is a display plus creative return the image
          if (decodedMetadata && decodedMetadata.template) {
            return {
              image: addCropSettingsToCreativeImage(imgObjMT),
              creativeData: {
                creativeMeta: decodedMetadata,
                creativeId: imageId,
              },
            };
          }

          return processNewlyAddedImages({
            imgObjMT,
            imageSource,
            isDisplayAds,
            isPortraitImage,
            mediaRepository,
            isAudienceAdSmartCropEnabled,
            isBrandKit,
          });
        })));
    });

    return Promise.all(addImagePromises)
      .then(processedImages => onNewImageSelected(processedImages))
      .catch((e) => {
        setImagePickerErrorWithErrorCodeError(e);
      });
  }, [imageRepository, isAudienceAdSmartCropEnabled, isDisplayAds, isPortraitImage, mediaRepository, onNewImageSelected, setImagePickerErrorWithErrorCodeError]);

  return {
    imagePickerError,
    setImagePickerError: setImagePickerErrorWithErrorCodeError,
    clearImagePickerError,
    uploadImage,
    processUploadedImage,
    onProcessUploadComplete,
    minSize,
    minUploadSize,
    supportImageUpscale,
    addImageByUrl,
    addCreatives,
    getStockImages,
    getInvestImages,
    onSelectStockImage,
    onImageSelected,
    socialMediaDataSource,
    customMediaSourceConfig,
    onImagesSelected,
  };
};
